import Cart from 'cart-store';

/*
  Keeps the installation line tied to the product it was bought with.

  Two jobs:

  1. The installation line gets no quantity control and no remove button, so a
     customer cannot strip installation out of an order and check out without it.
  2. When the last product that needs installation leaves the cart, the
     installation line goes with it, rather than sitting there on its own.

  This has to live in JavaScript. The cart rebuilds every row from the
  `template-cart-item` <template> after each change, and that template is
  rendered with no line item, so the Liquid in cart-item.liquid only ever
  applies to the first server render. Marking the row here covers the drawer,
  the cart page, and every re-render after an add or a quantity change.

  Only lines this theme added are touched: ls-install-estimator.js tags the
  product with `_installation_required` and the installation with
  `_installation_auto`. A cart built before those markers existed is left alone.
*/

const INSTALL_HANDLE = 'installation';
const REQUIRES_INSTALL = '_installation_required';
const AUTO_ADDED = '_installation_auto';
const LOCKED_CLASS = 'cart-item--install-locked';

const isInstallLine = (item) => Boolean(item) && item.handle === INSTALL_HANDLE;

const hasProperty = (item, name) => Boolean(item && item.properties && item.properties[name]);

function currentCart() {
  return Cart.getState().cart || { items: [] };
}

/* Every installation row, wherever it is rendered, loses its controls. */
function markRows(cart) {
  const installKeys = new Set(
    (cart.items || []).filter(isInstallLine).map((item) => item.key),
  );

  document.querySelectorAll('cart-item[key]').forEach((row) => {
    row.classList.toggle(LOCKED_CLASS, installKeys.has(row.getAttribute('key')));
  });
}

/* Guard against re-entering while our own change request is in flight — the
   removal updates the cart, which fires the subscriber again. */
let removing = false;

async function dropOrphanedInstallation(cart) {
  if (removing) return;

  const items = cart.items || [];
  const installation = items.find((item) => isInstallLine(item) && hasProperty(item, AUTO_ADDED));

  if (!installation) return;
  if (items.some((item) => hasProperty(item, REQUIRES_INSTALL))) return;

  removing = true;
  try {
    await Cart.change({ id: installation.key, quantity: 0 });
  } catch (error) {
    /* If the cart refuses the change, leave the line visible rather than
       trapping the customer with a row they cannot see or remove. */
    document.querySelectorAll(`cart-item[key="${installation.key}"]`).forEach((row) => {
      row.classList.remove(LOCKED_CLASS);
    });
  } finally {
    removing = false;
  }
}

function sync() {
  const cart = currentCart();
  markRows(cart);
  dropOrphanedInstallation(cart);
}

Cart.subscribe(sync);
sync();

/* Rows are cloned into the list by the theme's own cart subscriber, and the
   drawer builds its contents from a template the first time it opens. Either
   can land after our subscriber has run, so re-mark when the DOM changes.
   Batched to one pass per frame, and class changes are attribute mutations, so
   marking a row cannot re-trigger this. */
let queued = false;

new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    markRows(currentCart());
  });
}).observe(document.body, { childList: true, subtree: true });
